import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useQueryState } from 'nuqs';
import { resolvePortfolioUrlToPersist, resolveReferrerToPersist, resolveUtmReferrerValues } from '@/hooks/resolve-utm-referrer-values';

// UTM parameter keys
const UTM_KEYS = {
  REFERRER: '__utm_referrer',
  SOURCE: '__utm_source',
  CAMPAIGN: '__utm_campaign',
  MEDIUM: '__utm_medium',
  CONTENT: '__utm_content',
  PARTNER: '__partner',
  PORTFOLIO_CODE: '__portfolio_code',
  PORTFOLIO_URL: '__portfolio_url'
} as const;

// Click ID keys (per-platform, captured from URL and persisted)
const CLICK_ID_KEYS = {
  TTCLID: '__ttclid',
  GCLID: '__gclid',
  FBCLID: '__fbclid',
  MSCLKID: '__msclkid',
  TTP: '__ttp'
} as const;

// Read a cookie by name. Browser-only — returns '' on the server.
function readCookie(name: string): string {
  if (typeof document === 'undefined') {
    return '';
  }
  const prefix = `${name}=`;
  const match = document.cookie.split('; ').find(row => row.startsWith(prefix));
  return match ? decodeURIComponent(match.slice(prefix.length)) : '';
}

// Utility function to check if localStorage is available
function isLocalStorageAvailable(): boolean {
  try {
    const test = '__test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

// Safe localStorage hook that handles errors gracefully
function useSafeLocalStorage(key, defaultValue) {
  const $ = _c(6);
  const [value, setValue] = useLocalStorage(key, defaultValue);
  let t0;
  if ($[0] !== key || $[1] !== setValue) {
    t0 = newValue => {
      ;
      try {
        if (isLocalStorageAvailable()) {
          setValue(newValue);
        }
      } catch (t1) {
        const error = t1;
        console.warn(`Failed to set ${key} in localStorage:`, error);
      }
    };
    $[0] = key;
    $[1] = setValue;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const safeSetValue = t0;
  let t1;
  if ($[3] !== safeSetValue || $[4] !== value) {
    t1 = [value, safeSetValue];
    $[3] = safeSetValue;
    $[4] = value;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}

/**
 * Centralized UTM parameters hook that manages both URL query params and localStorage
 * Automatically syncs URL params to localStorage and provides a unified interface
 */
export function useUtmParams() {
  const $ = _c(117);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      defaultValue: ""
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [urlReferrer] = useQueryState("referrer", t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      defaultValue: ""
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [urlUtmReferrer] = useQueryState("utm_referrer", t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      defaultValue: ""
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [urlPortfolioUrl] = useQueryState("portfolio_url", t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      defaultValue: ""
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [urlSource] = useQueryState("utm_source", t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      defaultValue: ""
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [urlCampaign] = useQueryState("utm_campaign", t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      defaultValue: ""
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const [urlMedium] = useQueryState("utm_medium", t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      defaultValue: ""
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const [urlContent] = useQueryState("utm_content", t6);
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      defaultValue: ""
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const [urlPartner] = useQueryState("partner", t7);
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      defaultValue: ""
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const [urlPortfolioCode] = useQueryState("portfolio_code", t8);
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      defaultValue: ""
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const [urlTtclid] = useQueryState("ttclid", t9);
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      defaultValue: ""
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const [urlGclid] = useQueryState("gclid", t10);
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      defaultValue: ""
    };
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const [urlFbclid] = useQueryState("fbclid", t11);
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      defaultValue: ""
    };
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const [urlMsclkid] = useQueryState("msclkid", t12);
  const [storedReferrer, setStoredReferrer] = useSafeLocalStorage(UTM_KEYS.REFERRER, "");
  const [storedSource, setStoredSource] = useSafeLocalStorage(UTM_KEYS.SOURCE, "");
  const [storedCampaign, setStoredCampaign] = useSafeLocalStorage(UTM_KEYS.CAMPAIGN, "");
  const [storedMedium, setStoredMedium] = useSafeLocalStorage(UTM_KEYS.MEDIUM, "");
  const [storedContent, setStoredContent] = useSafeLocalStorage(UTM_KEYS.CONTENT, "");
  const [storedPartner, setStoredPartner] = useSafeLocalStorage(UTM_KEYS.PARTNER, "");
  const [storedPortfolioCode, setStoredPortfolioCode] = useSafeLocalStorage(UTM_KEYS.PORTFOLIO_CODE, "");
  const [storedPortfolioUrl, setStoredPortfolioUrl] = useSafeLocalStorage(UTM_KEYS.PORTFOLIO_URL, "");
  const [storedTtclid, setStoredTtclid] = useSafeLocalStorage(CLICK_ID_KEYS.TTCLID, "");
  const [storedGclid, setStoredGclid] = useSafeLocalStorage(CLICK_ID_KEYS.GCLID, "");
  const [storedFbclid, setStoredFbclid] = useSafeLocalStorage(CLICK_ID_KEYS.FBCLID, "");
  const [storedMsclkid, setStoredMsclkid] = useSafeLocalStorage(CLICK_ID_KEYS.MSCLKID, "");
  const [storedTtp, setStoredTtp] = useSafeLocalStorage(CLICK_ID_KEYS.TTP, "");
  let t13;
  if ($[13] !== storedPortfolioUrl || $[14] !== storedReferrer || $[15] !== urlPortfolioUrl || $[16] !== urlReferrer || $[17] !== urlUtmReferrer) {
    t13 = resolveUtmReferrerValues({
      urlReferrer,
      urlUtmReferrer,
      urlPortfolioUrl,
      storedReferrer,
      storedPortfolioUrl
    });
    $[13] = storedPortfolioUrl;
    $[14] = storedReferrer;
    $[15] = urlPortfolioUrl;
    $[16] = urlReferrer;
    $[17] = urlUtmReferrer;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const {
    referrer,
    portfolioUrl
  } = t13;
  const source = urlSource || storedSource || "";
  const campaign = urlCampaign || storedCampaign || "";
  const medium = urlMedium || storedMedium || "";
  const content = urlContent || storedContent || "";
  const partner = urlPartner || storedPartner || "";
  const portfolioCode = urlPortfolioCode || storedPortfolioCode || "";
  const ttclid = urlTtclid || storedTtclid || "";
  const gclid = urlGclid || storedGclid || "";
  const fbclid = urlFbclid || storedFbclid || "";
  const msclkid = urlMsclkid || storedMsclkid || "";
  const ttp = storedTtp || "";
  let t14;
  let t15;
  if ($[19] !== setStoredPortfolioUrl || $[20] !== storedPortfolioUrl || $[21] !== urlPortfolioUrl || $[22] !== urlReferrer) {
    t14 = () => {
      const portfolioUrlValue = resolvePortfolioUrlToPersist({
        urlPortfolioUrl,
        urlReferrer
      });
      if (portfolioUrlValue && portfolioUrlValue !== storedPortfolioUrl) {
        setStoredPortfolioUrl(portfolioUrlValue);
      }
    };
    t15 = [urlPortfolioUrl, urlReferrer, storedPortfolioUrl, setStoredPortfolioUrl];
    $[19] = setStoredPortfolioUrl;
    $[20] = storedPortfolioUrl;
    $[21] = urlPortfolioUrl;
    $[22] = urlReferrer;
    $[23] = t14;
    $[24] = t15;
  } else {
    t14 = $[23];
    t15 = $[24];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[25] !== setStoredReferrer || $[26] !== storedReferrer || $[27] !== urlReferrer || $[28] !== urlUtmReferrer) {
    t16 = () => {
      const referrerValue = resolveReferrerToPersist({
        urlReferrer,
        urlUtmReferrer
      });
      if (referrerValue && referrerValue !== storedReferrer) {
        setStoredReferrer(referrerValue);
      }
    };
    t17 = [urlReferrer, urlUtmReferrer, storedReferrer, setStoredReferrer];
    $[25] = setStoredReferrer;
    $[26] = storedReferrer;
    $[27] = urlReferrer;
    $[28] = urlUtmReferrer;
    $[29] = t16;
    $[30] = t17;
  } else {
    t16 = $[29];
    t17 = $[30];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[31] !== setStoredSource || $[32] !== storedSource || $[33] !== urlSource) {
    t18 = () => {
      if (urlSource && urlSource !== storedSource) {
        setStoredSource(urlSource);
      }
    };
    t19 = [urlSource, storedSource, setStoredSource];
    $[31] = setStoredSource;
    $[32] = storedSource;
    $[33] = urlSource;
    $[34] = t18;
    $[35] = t19;
  } else {
    t18 = $[34];
    t19 = $[35];
  }
  useEffect(t18, t19);
  let t20;
  let t21;
  if ($[36] !== setStoredCampaign || $[37] !== storedCampaign || $[38] !== urlCampaign) {
    t20 = () => {
      if (urlCampaign && urlCampaign !== storedCampaign) {
        setStoredCampaign(urlCampaign);
      }
    };
    t21 = [urlCampaign, storedCampaign, setStoredCampaign];
    $[36] = setStoredCampaign;
    $[37] = storedCampaign;
    $[38] = urlCampaign;
    $[39] = t20;
    $[40] = t21;
  } else {
    t20 = $[39];
    t21 = $[40];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[41] !== setStoredMedium || $[42] !== storedMedium || $[43] !== urlMedium) {
    t22 = () => {
      if (urlMedium && urlMedium !== storedMedium) {
        setStoredMedium(urlMedium);
      }
    };
    t23 = [urlMedium, storedMedium, setStoredMedium];
    $[41] = setStoredMedium;
    $[42] = storedMedium;
    $[43] = urlMedium;
    $[44] = t22;
    $[45] = t23;
  } else {
    t22 = $[44];
    t23 = $[45];
  }
  useEffect(t22, t23);
  let t24;
  let t25;
  if ($[46] !== setStoredContent || $[47] !== storedContent || $[48] !== urlContent) {
    t24 = () => {
      if (urlContent && urlContent !== storedContent) {
        setStoredContent(urlContent);
      }
    };
    t25 = [urlContent, storedContent, setStoredContent];
    $[46] = setStoredContent;
    $[47] = storedContent;
    $[48] = urlContent;
    $[49] = t24;
    $[50] = t25;
  } else {
    t24 = $[49];
    t25 = $[50];
  }
  useEffect(t24, t25);
  let t26;
  let t27;
  if ($[51] !== setStoredPartner || $[52] !== storedPartner || $[53] !== urlPartner) {
    t26 = () => {
      if (urlPartner && urlPartner !== storedPartner) {
        setStoredPartner(urlPartner);
      }
    };
    t27 = [urlPartner, storedPartner, setStoredPartner];
    $[51] = setStoredPartner;
    $[52] = storedPartner;
    $[53] = urlPartner;
    $[54] = t26;
    $[55] = t27;
  } else {
    t26 = $[54];
    t27 = $[55];
  }
  useEffect(t26, t27);
  let t28;
  let t29;
  if ($[56] !== setStoredPortfolioCode || $[57] !== storedPortfolioCode || $[58] !== urlPortfolioCode) {
    t28 = () => {
      if (urlPortfolioCode && urlPortfolioCode !== storedPortfolioCode) {
        setStoredPortfolioCode(urlPortfolioCode);
      }
    };
    t29 = [urlPortfolioCode, storedPortfolioCode, setStoredPortfolioCode];
    $[56] = setStoredPortfolioCode;
    $[57] = storedPortfolioCode;
    $[58] = urlPortfolioCode;
    $[59] = t28;
    $[60] = t29;
  } else {
    t28 = $[59];
    t29 = $[60];
  }
  useEffect(t28, t29);
  let t30;
  let t31;
  if ($[61] !== setStoredTtclid || $[62] !== storedTtclid || $[63] !== urlTtclid) {
    t30 = () => {
      if (urlTtclid && urlTtclid !== storedTtclid) {
        setStoredTtclid(urlTtclid);
      }
    };
    t31 = [urlTtclid, storedTtclid, setStoredTtclid];
    $[61] = setStoredTtclid;
    $[62] = storedTtclid;
    $[63] = urlTtclid;
    $[64] = t30;
    $[65] = t31;
  } else {
    t30 = $[64];
    t31 = $[65];
  }
  useEffect(t30, t31);
  let t32;
  let t33;
  if ($[66] !== setStoredGclid || $[67] !== storedGclid || $[68] !== urlGclid) {
    t32 = () => {
      if (urlGclid && urlGclid !== storedGclid) {
        setStoredGclid(urlGclid);
      }
    };
    t33 = [urlGclid, storedGclid, setStoredGclid];
    $[66] = setStoredGclid;
    $[67] = storedGclid;
    $[68] = urlGclid;
    $[69] = t32;
    $[70] = t33;
  } else {
    t32 = $[69];
    t33 = $[70];
  }
  useEffect(t32, t33);
  let t34;
  let t35;
  if ($[71] !== setStoredFbclid || $[72] !== storedFbclid || $[73] !== urlFbclid) {
    t34 = () => {
      if (urlFbclid && urlFbclid !== storedFbclid) {
        setStoredFbclid(urlFbclid);
      }
    };
    t35 = [urlFbclid, storedFbclid, setStoredFbclid];
    $[71] = setStoredFbclid;
    $[72] = storedFbclid;
    $[73] = urlFbclid;
    $[74] = t34;
    $[75] = t35;
  } else {
    t34 = $[74];
    t35 = $[75];
  }
  useEffect(t34, t35);
  let t36;
  let t37;
  if ($[76] !== setStoredMsclkid || $[77] !== storedMsclkid || $[78] !== urlMsclkid) {
    t36 = () => {
      if (urlMsclkid && urlMsclkid !== storedMsclkid) {
        setStoredMsclkid(urlMsclkid);
      }
    };
    t37 = [urlMsclkid, storedMsclkid, setStoredMsclkid];
    $[76] = setStoredMsclkid;
    $[77] = storedMsclkid;
    $[78] = urlMsclkid;
    $[79] = t36;
    $[80] = t37;
  } else {
    t36 = $[79];
    t37 = $[80];
  }
  useEffect(t36, t37);
  let t38;
  let t39;
  if ($[81] !== setStoredTtp || $[82] !== storedTtp) {
    t38 = () => {
      const ttpCookie = readCookie("_ttp");
      if (ttpCookie && ttpCookie !== storedTtp) {
        setStoredTtp(ttpCookie);
      }
    };
    t39 = [storedTtp, setStoredTtp];
    $[81] = setStoredTtp;
    $[82] = storedTtp;
    $[83] = t38;
    $[84] = t39;
  } else {
    t38 = $[83];
    t39 = $[84];
  }
  useEffect(t38, t39);
  let t40;
  let t41;
  if ($[85] !== setStoredTtclid || $[86] !== storedTtclid || $[87] !== urlTtclid) {
    t40 = () => {
      if (urlTtclid) {
        return;
      }
      const ttclidCookie = readCookie("ttclid");
      if (ttclidCookie && ttclidCookie !== storedTtclid) {
        setStoredTtclid(ttclidCookie);
      }
    };
    t41 = [urlTtclid, storedTtclid, setStoredTtclid];
    $[85] = setStoredTtclid;
    $[86] = storedTtclid;
    $[87] = urlTtclid;
    $[88] = t40;
    $[89] = t41;
  } else {
    t40 = $[88];
    t41 = $[89];
  }
  useEffect(t40, t41);
  let t42;
  if ($[90] !== campaign || $[91] !== content || $[92] !== fbclid || $[93] !== gclid || $[94] !== medium || $[95] !== msclkid || $[96] !== partner || $[97] !== portfolioCode || $[98] !== portfolioUrl || $[99] !== referrer || $[100] !== setStoredCampaign || $[101] !== setStoredContent || $[102] !== setStoredFbclid || $[103] !== setStoredGclid || $[104] !== setStoredMedium || $[105] !== setStoredMsclkid || $[106] !== setStoredPartner || $[107] !== setStoredPortfolioCode || $[108] !== setStoredPortfolioUrl || $[109] !== setStoredReferrer || $[110] !== setStoredSource || $[111] !== setStoredTtclid || $[112] !== setStoredTtp || $[113] !== source || $[114] !== ttclid || $[115] !== ttp) {
    t42 = {
      referrer,
      portfolioUrl,
      source,
      campaign,
      medium,
      content,
      partner,
      portfolioCode,
      ttclid,
      gclid,
      fbclid,
      msclkid,
      ttp,
      setReferrer: setStoredReferrer,
      setSource: setStoredSource,
      setCampaign: setStoredCampaign,
      setMedium: setStoredMedium,
      setContent: setStoredContent,
      setPartner: setStoredPartner,
      setPortfolioCode: setStoredPortfolioCode,
      setPortfolioUrl: setStoredPortfolioUrl,
      setTtclid: setStoredTtclid,
      setGclid: setStoredGclid,
      setFbclid: setStoredFbclid,
      setMsclkid: setStoredMsclkid,
      setTtp: setStoredTtp
    };
    $[90] = campaign;
    $[91] = content;
    $[92] = fbclid;
    $[93] = gclid;
    $[94] = medium;
    $[95] = msclkid;
    $[96] = partner;
    $[97] = portfolioCode;
    $[98] = portfolioUrl;
    $[99] = referrer;
    $[100] = setStoredCampaign;
    $[101] = setStoredContent;
    $[102] = setStoredFbclid;
    $[103] = setStoredGclid;
    $[104] = setStoredMedium;
    $[105] = setStoredMsclkid;
    $[106] = setStoredPartner;
    $[107] = setStoredPortfolioCode;
    $[108] = setStoredPortfolioUrl;
    $[109] = setStoredReferrer;
    $[110] = setStoredSource;
    $[111] = setStoredTtclid;
    $[112] = setStoredTtp;
    $[113] = source;
    $[114] = ttclid;
    $[115] = ttp;
    $[116] = t42;
  } else {
    t42 = $[116];
  }
  return t42;
}